import React, { useCallback, useContext, useRef, useState } from 'react';
import classNames from 'classnames';
import { ENQUIRY_ROUTES } from '../../utils';
import EnquiryListingContext from '../../providers/EnquiryListing';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PasswordField from 'components/PasswordField';
import ReCAPTCHA from 'react-google-recaptcha';
import env from 'env';
import { ReactComponent as LinkedIn } from 'icons/linkedin.svg';
import { ReactComponent as Google } from 'icons/google.svg';
import { ReactComponent as Facebook } from 'icons/facebook.svg';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import utils, {
  getEmailOrPhoneForLoginOrRegister,
} from '../../../../../../../../../utils';
import { RegisterForm } from 'models/AuthBody';
import AuthContext from 'providers/Auth/Auth.context';
import EmailOrPhoneFieldWrapper from 'components/EmailOrPhoneFieldWrapper';
import useEnquiryReroute from '../../hooks/useEnquiryReroute';
import credentialsService from 'services/credentialsService';

import './EnquirySliderSignUp.styles.scss';
import './EnquirySliderSignUp.styles.responsive.scss';

const EnquirySliderSignUp: React.FC = () => {
  const [socialMediaOpen, setSocialMediaOpen] = useState(false);

  const { popServerError, popError } = useContext(PopNotificationsContext);
  const { onLoginReq } = useContext(AuthContext);
  const { listingId, setEnquiryRoute } = useContext(EnquiryListingContext);

  const recaptchaRef = useRef(null);
  const { t } = useTranslation();

  const location = useEnquiryReroute();
  const commonValidations = useValidations();

  const classes = classNames('enquiry-slider-sign-up');

  const onSubmit = useCallback(
    async (values: RegisterForm) => {
      const recaptchaResponseToken = recaptchaRef.current?.getValue();

      if (!recaptchaResponseToken) {
        popError(t('recaptchaRequired'));
        return;
      }

      const { email, emailOrPhone, ...rest } = values;
      const requestData = {
        recaptchaResponseToken,
        listingId,
        ...rest,
        ...getEmailOrPhoneForLoginOrRegister({ email, emailOrPhone }),
      };

      try {
        const { data: authBody } = await api.auth.register(requestData);

        credentialsService.justRegistered();

        onLoginReq(authBody, location);
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popError, t, onLoginReq, location, popServerError],
  );

  return (
    <div className={classes}>
      <p className="enquiry-title">{t('firstCreateAccount')}</p>

      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}

            <div>
              <Field
                name="firstName"
                placeholder={t('firstName')}
                label={t('firstName')}
                component={InputField}
                required
                validate={commonValidations.name}
              />
              <Field
                name="lastName"
                placeholder={t('lastName')}
                label={t('lastName')}
                component={InputField}
                required
                validate={commonValidations.name}
              />
              <EmailOrPhoneFieldWrapper />
              <Field
                name="password"
                label={t('password')}
                placeholder={t('password')}
                component={PasswordField}
                validate={commonValidations.password}
                required
              />
              {!utils.isSSG() && (
                <ReCAPTCHA
                  className="recaptcha"
                  ref={recaptchaRef}
                  sitekey={env.ReCAPTCHA_KEY}
                />
              )}
            </div>
            <div
              className={classNames('enquiry-slider-sign-up__social-media', {
                open: socialMediaOpen,
              })}
            >
              <Button
                onClick={() => setSocialMediaOpen((prev) => !prev)}
                variant="link"
                iconPosition="right"
                icon={ArrowdownIcon}
              >
                {t('signInWithSocialMedia')}
              </Button>
              <div className="links">
                <a href={`${env.SERVER_ENDPOINT}/api/auth/linkedin`}>
                  <LinkedIn />
                  LinkedIn
                </a>
                <a href={`${env.SERVER_ENDPOINT}/api/auth/facebook`}>
                  <Facebook />
                  Facebook
                </a>
                <a href={`${env.SERVER_ENDPOINT}/api/auth/google`}>
                  <Google />
                  Google
                </a>
              </div>
            </div>
            <Button
              variant="link"
              className="login-button"
              icon={LockclosedIcon}
              onClick={() => setEnquiryRoute(ENQUIRY_ROUTES.LOGIN)}
            >
              {t('haveAnAccountLogin')}
            </Button>
            <div className="enquiry-slider-sign-up__actions">
              <span />

              <Button type="submit" disabled={submitting}>
                {t('createAccount')}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default EnquirySliderSignUp;
